import "./PageNumberComboBox.scss";

import { Listbox } from "@headlessui/react";
import clsx from "clsx";
import { useTranslation } from "react-i18next";
import { useSearchParams } from "react-router";
import { FCC } from "types";

import { ChevronRight } from "@/assets/icons/icons";
import { FocusRing } from "@/components/Layout/BaseLayouts/Footer/FocusRing";
import {
  clampPageSize,
  DEFAULT_PAGE_SIZE,
  PAGE_SIZES,
} from "@/components/Layout/Table/PageNumberComboBox/pageSize";
import { KEYBOARD_TAB_INDEX } from "@/utils/keyboardTabIndex";

const PAGE_SIZE_OPTIONS = PAGE_SIZES.map(String);

/**
 * Table footer control: a caption, the current value and a chevron on one
 * line, without any field chrome.
 *
 * The flyout reuses the dark panel of the project's other dropdowns (see
 * ListBox.scss) so it reads the same as e.g. the language switcher, and it
 * opens upward because this control always sits at the bottom of its card.
 *
 * `defaultPageSize` must mirror what the calling list actually falls back to
 * when the query param is absent — otherwise the footer advertises a page size
 * that was never requested (the bills list uses 5 on mobile, 10 on desktop).
 */
export const PageNumberComboBox: FCC<{ defaultPageSize?: number }> = ({
  defaultPageSize = DEFAULT_PAGE_SIZE,
}) => {
  const { t } = useTranslation("portal");
  const [searchParams, setSearchParams] = useSearchParams();
  // Same clamp the callers apply to the request, so display and fetch agree.
  const currentlyActive = `${clampPageSize(
    searchParams.get("pageSize"),
    defaultPageSize,
  )}`;

  // A bigger page size shrinks the page count, so staying on the current page
  // can land out of range — the list would fetch an empty page and only correct
  // itself once the response arrives. Drop back to the first page instead.
  const changePageSize = (value: string) => {
    setSearchParams((params) => {
      params.set("pageSize", value);
      params.delete("page");

      return params;
    });
  };

  return (
    <Listbox value={currentlyActive} onChange={changePageSize}>
      {({ open }) => (
        <div className="page-number-combo-box">
          {/* // TODO: build NavItem Component */}
          <Listbox.Button
            data-testid="pageSize"
            className="combobox-button group relative outline-none"
            tabIndex={KEYBOARD_TAB_INDEX}
          >
            <FocusRing />
            <span className="caption">
              {t("layout.table.pageNumberComboBox.entriesPerPage")}
            </span>
            <span className="value ml-2 !text-body-xs">{currentlyActive}</span>
            {/* The footer chevron is the same glyph as the pagination
                chevrons, rotated a quarter turn; it flips once the flyout
                is open, like the other dropdowns. */}
            <ChevronRight
              className={clsx(
                "size-4 shrink-0 transition-transform",
                open ? "-rotate-90" : "rotate-90",
              )}
              aria-hidden="true"
            />
          </Listbox.Button>
          <Listbox.Options className="combobox-options" modal={false}>
            {PAGE_SIZE_OPTIONS.map((size) => (
              <Listbox.Option
                key={size}
                className={({ active }) => clsx("option", active && "active")}
                value={size}
              >
                {({ selected }) => (
                  <>
                    {size}
                    {selected && (
                      <span className="selected-indicator" aria-hidden="true" />
                    )}
                  </>
                )}
              </Listbox.Option>
            ))}
          </Listbox.Options>
        </div>
      )}
    </Listbox>
  );
};
